import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import type { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import {
	FormViewMode,
	useFormIndexActions,
	useSelectedFormStoreState,
} from '@atlassian/proforma-form-system';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { useSelectFormNoDiscardChangeNotice } from '../use-handle-form-selected/index.tsx';

export const useHandleEditForm = (
	loadFormStore: (formId: number) => Promise<FormStore>,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { selectedFormStore } = useSelectedFormStoreState();
	const { setFormViewMode, reportError } = useFormIndexActions();
	const selectFormNoDiscardChangeNotice = useSelectFormNoDiscardChangeNotice(
		loadFormStore,
		disableIssueViewFullPageDropzoneCallback,
	);

	return useCallback(
		async (formId: number, formStore?: FormStore) => {
			try {
				if (selectedFormStore?.answersModified) {
					throw new Error('"Edit form" not allowed while editing form');
				}
				const isCurrentFormSelected = !!formStore;
				if (!isCurrentFormSelected) {
					await selectFormNoDiscardChangeNotice(formId);
				}
				setFormViewMode(FormViewMode.Edit);
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`${ActionSubject.FORM_EDIT} ${Action.STARTED}`,
					{
						formId,
					},
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				reportError(error, {
					id: 'editForm',
					attributes: { formId },
				});
				return Promise.reject(error);
			}
		},
		[
			createAnalyticsEvent,
			reportError,
			selectFormNoDiscardChangeNotice,
			selectedFormStore?.answersModified,
			setFormViewMode,
		],
	);
};
